import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { ru } from './locales/ru'
import { ua } from './locales/ua'
import { en } from './locales/en'
import { useUIStore, type Lang } from '@/stores/ui'

/**
 * i18n bootstrap. Replaces the legacy 5286-line `lang-data.js` + `t()` global.
 *
 * Default locale: `ua` (matches legacy frontend default and current user
 * preference). Legacy code stored value `uk` for Ukrainian — we accept both
 * and normalize to `ua` here to match backend's `notification_i18n.t()`
 * convention.
 *
 * Fallback locale: `ru` (architecture §8.1 — `ua` is default, `ru` is the
 * last-resort lookup). `en` is shipped only as a developer-facing safety
 * net and is intentionally omitted from the user-selectable language list
 * (`useUIStore.lang` is `'ua' | 'ru'`).
 */
const SUPPORTED = ['ua', 'ru'] as const

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { ua, ru, en },
    // Initial language: prefer the value already in the UI store (which has
    // been hydrated from `localStorage 'lang'` by zustand-persist), then let
    // the detector chain (localStorage > navigator) take over for first-run
    // visitors with no stored preference.
    lng: useUIStore.getState().lang,
    fallbackLng: 'ru',
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // First-run only: if `useUIStore.getState().lang` were missing we'd
      // fall back to the legacy `lang` key, then to `navigator`. Persistence
      // is owned by `useUIStore` (`creo-ui` envelope) — we explicitly DO NOT
      // cache here, otherwise i18n would resurrect the legacy `lang` key
      // that the store's one-shot migration is trying to delete.
      order: ['localStorage', 'navigator'],
      caches: [],
      lookupLocalStorage: 'lang',
      convertDetectedLanguage: (l) => (l === 'uk' ? 'ua' : l),
    },
  })

/**
 * Two-way sync between i18next and the UI store. Either side can be the
 * source of truth at any moment:
 *
 * - User toggles language in the UI → `useUIStore.setLang('ru')` →
 *   subscriber here calls `i18n.changeLanguage('ru')`.
 * - LanguageDetector picks a locale on first load that differs from the
 *   store default → `i18n` `languageChanged` event flows back into the
 *   store so persisted state stays consistent.
 *
 * The guards prevent infinite loops by bailing out when the value already
 * matches.
 */
useUIStore.subscribe((state, prev) => {
  if (state.lang !== prev.lang && i18n.language !== state.lang) {
    void i18n.changeLanguage(state.lang)
  }
})

i18n.on('languageChanged', (lng) => {
  const normalized: Lang = lng === 'ru' ? 'ru' : 'ua'
  if (useUIStore.getState().lang !== normalized) {
    useUIStore.setState({ lang: normalized })
  }
})

export default i18n
